import React, { useState, useContext } from 'react';
import { CONTENT_WIDTH } from "../../constants/layout";
import { 
    Box, 
    Typography, 
    useTheme,
    Snackbar,
    Modal,
    ModalDialog
} from '@mui/joy';
import axios from 'axios';
import CustomInput from "../../components/UI/custom_elements/customInput";
import PrimaryButton from "../../components/UI/custom_elements/primaryButton";
import SecondaryButton from "../../components/UI/custom_elements/secondaryButton";
import SessionContext from "../../store";

const PolygonIcon = () => (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="16" fill="white"/>
        <g clipPath="url(#clip0_2624_3374)">
            <path d="M20.9865 12.7229C20.6362 12.5227 20.1859 12.5227 19.7856 12.7229L16.9837 14.374L15.0823 15.4248L12.3303 17.076C11.9801 17.2761 11.5298 17.2761 11.1295 17.076L8.97797 15.775C8.62772 15.5749 8.37754 15.1746 8.37754 14.7243V12.2225C8.37754 11.8222 8.57768 11.422 8.97797 11.1718L11.1295 9.92088C11.4797 9.72074 11.9301 9.72074 12.3303 9.92088L14.4819 11.2218C14.8321 11.422 15.0823 11.8222 15.0823 12.2726V13.9237L16.9837 12.8229V11.1217C16.9837 10.7215 16.7835 10.3212 16.3832 10.071L12.3804 7.71932C12.0301 7.51918 11.5798 7.51918 11.1795 7.71932L7.07662 10.121C6.67633 10.3212 6.47619 10.7215 6.47619 11.1217V15.8251C6.47619 16.2254 6.67633 16.6256 7.07662 16.8758L11.1295 19.2275C11.4797 19.4276 11.9301 19.4276 12.3303 19.2275L15.0823 17.6264L16.9837 16.5256L19.7356 14.9244C20.0859 14.7243 20.5362 14.7243 20.9365 14.9244L23.088 16.1753C23.4382 16.3755 23.6884 16.7758 23.6884 17.2261V19.7278C23.6884 20.1281 23.4883 20.5284 23.088 20.7786L20.9865 22.0295C20.6362 22.2296 20.1859 22.2296 19.7856 22.0295L17.6341 20.7786C17.2839 20.5785 17.0337 20.1782 17.0337 19.7278V18.1267L15.1323 19.2275V20.8787C15.1323 21.279 15.3325 21.6792 15.7328 21.9294L19.7856 24.2811C20.1359 24.4812 20.5862 24.4812 20.9865 24.2811L25.0394 21.9294C25.3896 21.7293 25.6398 21.329 25.6398 20.8787V16.1253C25.6398 15.725 25.4397 15.3247 25.0394 15.0745L20.9865 12.7229Z" fill="#8247E5"/>
        </g>
        <defs>
            <clipPath id="clip0_2624_3374">
                <rect width="19.2136" height="16.7619" fill="white" transform="translate(6.47619 7.61902)"/>
            </clipPath>
        </defs>
    </svg>
);

const WithdrawalsPage = () => {
    const theme = useTheme();
    const { session } = useContext(SessionContext);
    
    // Estados del flujo
    const [step, setStep] = useState('input'); // 'input', 'preview', 'disclaimer', 'wallet', 'success'
    
    // Estados de datos
    const [amount, setAmount] = useState('');
    const [previewData, setPreviewData] = useState(null);
    const [walletAddress, setWalletAddress] = useState('');
    const [withdrawalResult, setWithdrawalResult] = useState(null);
    
    // Estados de loading
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingWithdrawal, setLoadingWithdrawal] = useState(false);
    
    // Estados de mensajes
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Estado del disclaimer
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

    const formatAmountUSDT = (value) => {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4
        }).format(numValue || 0);
    };

    const handlePreview = async () => {
        if (!amount || parseFloat(amount) <= 0) {
            setErrorMessage('Por favor ingrese un monto válido');
            return;
        }

        setLoadingPreview(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v2/crypto/withdrawal/preview`,
                { amount: parseFloat(amount) },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            if (response.data && response.data.success) {
                setPreviewData(response.data.data);
                setStep('preview');
            } else {
                setErrorMessage(response.data?.message || 'Error al generar el preview');
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message || error.response.data.errorMessage || 'Error al generar el preview');
            } else {
                setErrorMessage('Error al generar el preview');
            }
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleConfirmPreview = () => {
        setStep('disclaimer');
    };

    const handleCancelPreview = () => {
        setStep('input');
        setPreviewData(null);
    };

    const handleAcceptDisclaimer = () => {
        setDisclaimerAccepted(true);
    };

    const handleConfirmWithdrawal = async () => {
        if (!walletAddress.trim()) {
            setErrorMessage('Por favor ingrese la dirección de la wallet destino');
            return;
        }

        // Validación básica de dirección Ethereum/Polygon
        if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
            setErrorMessage('La dirección de wallet no es válida');
            return;
        }

        setLoadingWithdrawal(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v2/crypto/withdrawal/`,
                {
                    amount: parseFloat(amount),
                    destination_address: walletAddress
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            if (response.data && response.data.success) {
                setWithdrawalResult(response.data.data);
                setStep('success');
                setSuccessMessage('Retiro procesado exitosamente');
            } else {
                setErrorMessage(response.data?.message || 'Error al procesar el retiro');
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message || error.response.data.errorMessage || 'Error al procesar el retiro');
            } else {
                setErrorMessage('Error al procesar el retiro');
            }
        } finally {
            setLoadingWithdrawal(false);
        }
    };

    const handleNewWithdrawal = () => {
        setStep('input');
        setAmount('');
        setPreviewData(null);
        setWalletAddress('');
        setWithdrawalResult(null);
        setDisclaimerAccepted(false);
    };

    return (
        <Box mx={5} my={2} sx={{ width: CONTENT_WIDTH }}>
            <Snackbar
                open={!!errorMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                variant="solid"
                color="danger"
                autoHideDuration={4000}
                onClose={() => setErrorMessage('')}
            >
                {errorMessage}
            </Snackbar>
            
            <Snackbar
                open={!!successMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                variant="solid"
                color="success"
                autoHideDuration={4000}
                onClose={() => setSuccessMessage('')}
            >
                {successMessage}
            </Snackbar>

            {/* Modal Disclaimer Polygon */}
            <Modal open={step === 'disclaimer'} onClose={() => setStep('preview')}>
                <ModalDialog 
                    variant="plain" 
                    sx={{ 
                        minWidth: 550,
                        maxWidth: 550,
                        borderRadius: '24px',
                        backgroundColor: '#E0FAFA',
                        p: 4
                    }}
                >
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography sx={{ 
                            fontFamily: "Montserrat", 
                            fontSize: "20px", 
                            fontWeight: 700, 
                            color: '#082B39',
                            mb: 3
                        }}>
                            Muney usa la red
                        </Typography>
                        
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            px: 3,
                            py: 1.5,
                            mb: 3
                        }}>
                            <PolygonIcon />
                            <Typography sx={{ 
                                fontFamily: "Montserrat", 
                                fontSize: "16px", 
                                fontWeight: 600, 
                                color: '#082B39'
                            }}>
                                Polygon
                            </Typography>
                        </Box>

                        <Typography sx={{ 
                            fontFamily: "Montserrat", 
                            fontSize: "14px", 
                            color: '#082B39',
                            textAlign: 'center',
                            mb: 3,
                            lineHeight: 1.6
                        }}>
                            Asegúrate de que la wallet destino sea compatible con la red <strong>Polygon</strong>. 
                            Los fondos enviados a una dirección incorrecta no podrán ser recuperados.
                        </Typography>

                        <Typography sx={{ 
                            fontFamily: "Montserrat", 
                            fontSize: "12px", 
                            color: '#F20E25',
                            textAlign: 'center',
                            mb: 3,
                            fontWeight: 500
                        }}>
                            ⚠️ Esta acción es irreversible. Verifica bien la dirección antes de continuar.
                        </Typography>

                        {!disclaimerAccepted ? (
                            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                                <SecondaryButton 
                                    click={() => setStep('preview')} 
                                    text="Cancelar" 
                                    width="50%"
                                />
                                <Box 
                                    onClick={handleAcceptDisclaimer}
                                    sx={{
                                        flex: 1,
                                        backgroundColor: '#18E9E9',
                                        borderRadius: '12px',
                                        py: 1.5,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        '&:hover': {
                                            backgroundColor: '#15D4D4'
                                        }
                                    }}
                                >
                                    <Typography sx={{ 
                                        fontFamily: "Montserrat", 
                                        fontSize: "14px", 
                                        fontWeight: 600, 
                                        color: '#082B39'
                                    }}>
                                        Entiendo, continuar
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ width: '100%' }}>
                                <Typography sx={{ 
                                    fontFamily: "Montserrat", 
                                    fontSize: "14px", 
                                    fontWeight: 600,
                                    color: '#082B39',
                                    mb: 1
                                }}>
                                    Dirección de wallet destino (Polygon)
                                </Typography>
                                <CustomInput
                                    placeholder="0x..."
                                    width="100%"
                                    value={walletAddress}
                                    setValue={setWalletAddress}
                                    textColor="#082B39"
                                />
                                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                    <SecondaryButton 
                                        click={() => {
                                            setStep('preview');
                                            setDisclaimerAccepted(false);
                                            setWalletAddress('');
                                        }} 
                                        text="Cancelar" 
                                        width="50%"
                                    />
                                    <PrimaryButton 
                                        click={handleConfirmWithdrawal}
                                        text="Confirmar Retiro"
                                        loading={loadingWithdrawal}
                                        desactived={!walletAddress || loadingWithdrawal}
                                        width="50%"
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </ModalDialog>
            </Modal>

            <Typography sx={{
                fontFamily: "Montserrat",
                fontSize: "20px",
                fontWeight: 700,
                lineHeight: "120%",
                color: theme.vars.palette.primary["500"],
                mb: 4
            }}>
                Retiros USDT
            </Typography>

            <Box sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                p: 4, 
                backgroundColor: theme.vars.palette.background.surface, 
                borderRadius: '16px' 
            }}>
                {/* Step 1: Input de monto */}
                {step === 'input' && (
                    <Box>
                        <Typography sx={{ 
                            fontFamily: "Montserrat", 
                            fontSize: "18px", 
                            fontWeight: 600, 
                            color: theme.vars.palette.text.main,
                            mb: 3,
                            textAlign: 'center'
                        }}>
                            ¿Cuánto deseas retirar?
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ 
                                fontFamily: "Montserrat", 
                                fontSize: "14px", 
                                fontWeight: 500, 
                                color: theme.vars.palette.text.main,
                                mb: 1
                            }}>
                                Monto a retirar (USDT)
                            </Typography>
                            <CustomInput
                                placeholder="0.00"
                                width="100%"
                                value={amount}
                                setValue={setAmount}
                                type="number"
                            />
                        </Box>

                        <PrimaryButton 
                            click={handlePreview}
                            text="Generar Preview"
                            loading={loadingPreview}
                            desactived={!amount || parseFloat(amount) <= 0 || loadingPreview}
                            width="100%"
                        />
                    </Box>
                )}

                {/* Step 2: Preview */}
                {step === 'preview' && previewData && (
                    <Box>
                        <Typography sx={{ 
                            fontFamily: "Montserrat", 
                            fontSize: "18px", 
                            fontWeight: 600, 
                            color: theme.vars.palette.text.main,
                            mb: 3,
                            textAlign: 'center'
                        }}>
                            Resumen del Retiro
                        </Typography>

                        <Box sx={{ 
                            p: 3, 
                            borderRadius: '12px', 
                            backgroundColor: theme.vars.palette.background.level1,
                            border: `1px solid ${theme.vars.palette.neutral[200]}`,
                            mb: 3
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    Monto solicitado:
                                </Typography>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: theme.vars.palette.text.main }}>
                                    {formatAmountUSDT(previewData.amount_requested)} USDT
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    Comisión:
                                </Typography>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: theme.vars.palette.text.main }}>
                                    -{formatAmountUSDT(previewData.total_fee)} USDT
                                </Typography>
                            </Box>

                            <Box sx={{ 
                                borderTop: `1px solid ${theme.vars.palette.neutral[200]}`,
                                pt: 2,
                                display: 'flex', 
                                justifyContent: 'space-between'
                            }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 600, color: theme.vars.palette.text.main }}>
                                    Recibirás:
                                </Typography>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "18px", fontWeight: 700, color: theme.vars.palette.primary["500"] }}>
                                    {formatAmountUSDT(previewData.amount_to_receive)} USDT
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <SecondaryButton 
                                click={handleCancelPreview}
                                text="Cancelar"
                                width="50%"
                            />
                            <PrimaryButton 
                                click={handleConfirmPreview}
                                text="Confirmar y Continuar"
                                width="50%"
                            />
                        </Box>
                    </Box>
                )}

                {/* Step 4: Success */}
                {step === 'success' && withdrawalResult && (
                    <Box>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <Typography sx={{ 
                                fontFamily: "Montserrat", 
                                fontSize: "48px",
                                mb: 2
                            }}>
                                ✅
                            </Typography>
                            <Typography sx={{ 
                                fontFamily: "Montserrat", 
                                fontSize: "20px", 
                                fontWeight: 700, 
                                color: theme.vars.palette.primary["500"],
                                mb: 1
                            }}>
                                Tú Solicitud ha sido enviada
                            </Typography>
                            <Typography sx={{ 
                                fontFamily: "Montserrat", 
                                fontSize: "14px", 
                                color: theme.vars.palette.text.main
                            }}>
                                Tú solicitud ha sido enviada y se encuentra en proceso de autorización
                            </Typography>
                        </Box>

                        <Box sx={{ 
                            p: 3, 
                            borderRadius: '12px', 
                            backgroundColor: theme.vars.palette.background.level1,
                            border: `1px solid ${theme.vars.palette.neutral[200]}`,
                            mb: 3
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    ID de transacción:
                                </Typography>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", fontWeight: 500, color: theme.vars.palette.text.main }}>
                                    {withdrawalResult.id}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    Monto solicitado:
                                </Typography>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: theme.vars.palette.text.main }}>
                                    {formatAmountUSDT(withdrawalResult.total_requested)} USDT
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    Monto final:
                                </Typography>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 700, color: theme.vars.palette.primary["500"] }}>
                                    {formatAmountUSDT(withdrawalResult.total_final)} USDT
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    Wallet destino:
                                </Typography>
                                <Typography sx={{ 
                                    fontFamily: "Montserrat", 
                                    fontSize: "11px", 
                                    fontWeight: 500, 
                                    color: theme.vars.palette.text.main,
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {withdrawalResult.wallet_address}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    Estado:
                                </Typography>
                                <Typography sx={{ 
                                    fontFamily: "Montserrat", 
                                    fontSize: "14px", 
                                    fontWeight: 600, 
                                    color: ({ pending: '#FF9800', completed: '#4CAF50', success: '#4CAF50', failed: '#F44336' }[withdrawalResult.status]) || '#4CAF50',
                                    textTransform: 'capitalize'
                                }}>
                                    {({ pending: 'Pendiente', completed: 'Completado', success: 'Completado', failed: 'Fallido' }[withdrawalResult.status]) || withdrawalResult.status}
                                </Typography>
                            </Box>
                        </Box>

                        <PrimaryButton 
                            click={handleNewWithdrawal}
                            text="Realizar otro retiro"
                            width="100%"
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default WithdrawalsPage;
